import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  AccessibilityInfo,
  findNodeHandle,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import { withDeadline } from "@heraion/shared";
import { Button } from "../primitives/Button";
import { AgeDeclarationCheckbox } from "./AgeDeclarationCheckbox";
import { failureText, type AccountActionOutcome } from "./accountActions";
import { authFormStyles } from "./authFormStyles";
import type { UiTheme } from "../theme";
import { stateAria } from "../a11y";

// #175 (Antenore 19/10): the blocking screen for an account that has not yet
// declared the minimum age. It sits OVER the signed-in shell, never a wrapper
// that swaps its children: the app below stays mounted (a running logger or
// editor keeps its state). The tree shape never changes with `required`
// (children first, the gate after), so a late answer from the profile read
// never unmounts anything.
//
// ⛔ A top-level RN Modal, not an in-tree overlay: a sheet already open below
// (ExercisePicker, PlateCalc, a Sheet or Dialog) is itself a Modal, a separate
// native window on mobile and a portal on web, and an in-tree layer stays
// UNDER it, so the athlete could keep using the app past the gate. The gate's
// Modal is MOUNTED only while required, so it is presented after any sheet
// open at that moment and lands above it: a later Android dialog window, and
// on web a later portal div at the same z-index (react-native-web appends one
// per mounted Modal). The Modal also blocks touch, traps web focus and marks
// itself aria-modal; `onRequestClose` (Android back, web Escape) does nothing.
//
// ⛔ `required` comes from the profile the app ALREADY reads (mobile
// ProfileContext, web useProfileDoc): true only for a profile the server
// returned with no valid declaration. Not loaded, a failed read, a 404: false,
// so a network error never locks anyone out. Once the POST lands the host
// updates that source and `required` goes false; a withdrawal from Settings
// (#383) makes it true again at once.

type Fetcher = (
  path: string,
  opts?: { method?: "POST"; body?: unknown },
) => Promise<unknown>;

/** How long a save may take before the athlete is told it failed. */
export const AGE_DECLARATION_DEADLINE_MS = 15_000;

export type AgeDeclarationOverlayProps = {
  theme: UiTheme;
  /** A profile was read from the server and holds no valid declaration. */
  required: boolean;
  apiFetch: Fetcher;
  /** Brings the host's profile source up to date after the server recorded
   *  the declaration (a local mark, or a re-read). */
  onDeclared: () => void | Promise<void>;
  /** The host's own sign-out: the way out for someone who will not tick. */
  onSignOut: () => void;
  /** The host's delete-account flow, the one Settings → Account runs;
   *  `onBusy` is true while the request runs. Both mounts pass it; without
   *  it the screen offers sign-out alone. */
  onDeleteAccount?: (onBusy: (busy: boolean) => void) => Promise<AccountActionOutcome>;
  /** The host's frame around the screen (mobile: a SafeAreaView). */
  frame?: (screen: ReactNode) => ReactNode;
  children: ReactNode;
};

const stayOpen = () => {};

// A 409 is not stored: the route lost its write twice to concurrent ones,
// and a press again reads what they left.
const postDeclaration = async (apiFetch: Fetcher, at: string): Promise<void> => {
  await apiFetch("/api/profiles/age-declaration", { method: "POST", body: { at } });
};

export function AgeDeclarationOverlay({
  theme,
  required,
  apiFetch,
  onDeclared,
  onSignOut,
  onDeleteAccount,
  frame,
  children,
}: AgeDeclarationOverlayProps) {
  const { t } = useTranslation();
  const declare = useCallback(
    (at: string) =>
      withDeadline(
        (async () => {
          await postDeclaration(apiFetch, at);
          await onDeclared();
        })(),
        // No abort: a late answer is harmless, the route is write-once.
        () => undefined,
        AGE_DECLARATION_DEADLINE_MS,
        (ms) => new Error(`age_declaration_timeout_${ms}`),
      ),
    [apiFetch, onDeclared],
  );

  const screen = (
    <AgeDeclarationScreen
      theme={theme}
      onDeclare={declare}
      onSignOut={onSignOut}
      onDeleteAccount={onDeleteAccount}
    />
  );

  return (
    <>
      {children}
      {required ? (
        <Modal
          visible
          transparent
          animationType="none"
          onRequestClose={stayOpen}
          aria-label={t("auth.ageGate.title")}
        >
          <View
            style={[layer.fill, { backgroundColor: theme.palette.bg }]}
            accessibilityViewIsModal
          >
            {frame ? frame(screen) : screen}
          </View>
        </Modal>
      ) : null}
    </>
  );
}

const layer = StyleSheet.create({
  fill: { flex: 1 },
});

type ScreenProps = {
  theme: UiTheme;
  /** Saves the declaration ticked at `at`; rejects when it could not. */
  onDeclare: (at: string) => Promise<void>;
  onSignOut: () => void;
  onDeleteAccount?: AgeDeclarationOverlayProps["onDeleteAccount"];
};

export function AgeDeclarationScreen({
  theme,
  onDeclare,
  onSignOut,
  onDeleteAccount,
}: ScreenProps) {
  const { t } = useTranslation();
  const styles = authFormStyles(theme);
  // When the box was ticked (AgeDeclarationCheckbox dates it); null unticked.
  const [tickedAt, setTickedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<Text>(null);

  // A screen reader starts at the question. Native only: on web a focused
  // title draws a focus ring around plain text, and the header role is
  // enough for a reader to land on it.
  useEffect(() => {
    if (Platform.OS === "web") return;
    const node = titleRef.current;
    const tag = node === null ? null : findNodeHandle(node);
    if (tag !== null) AccessibilityInfo.setAccessibilityFocus(tag);
  }, []);

  const submit = async () => {
    if (tickedAt === null) return;
    setError(null);
    setBusy(true);
    try {
      await onDeclare(tickedAt);
      // Success: the host's profile now holds it and this screen goes.
    } catch (err) {
      showError(failureText(err, t, "auth.ageGate.saveFailed"));
    } finally {
      // Also after a success, in case the host's re-read did not land: the
      // athlete can press again (the route answers the stored declaration).
      setBusy(false);
    }
  };

  const showError = (message: string) => {
    setError(message);
    AccessibilityInfo.announceForAccessibility(message);
  };

  const deleteAccount = async () => {
    if (!onDeleteAccount) return;
    setError(null);
    try {
      const outcome = await onDeleteAccount(setDeleting);
      // Done: the host signs out and this screen goes with the session.
      if (typeof outcome === "object") showError(outcome.error);
    } catch {
      showError(t("mobile.account.actionFailed"));
    } finally {
      setDeleting(false);
    }
  };
  // Sign-out stays open during a save, as before; never during a delete.
  const deleteOff = busy || deleting;

  const unticked = tickedAt === null;
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      contentContainerStyle={{
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing.s5,
      }}
    >
      <View style={styles.wrap}>
        <Text ref={titleRef} style={styles.title} accessibilityRole="header">
          {t("auth.ageGate.title")}
        </Text>
        <Text style={styles.help}>{t("auth.ageGate.body")}</Text>
        <AgeDeclarationCheckbox
          theme={theme}
          tickedAt={tickedAt}
          onChange={setTickedAt}
          disabled={deleteOff}
          hintWhileUnticked
        />
        {error ? (
          <Text
            style={styles.error}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
            aria-live="polite"
          >
            {error}
          </Text>
        ) : null}
        <Button
          theme={theme}
          label={t("auth.ageGate.submit")}
          loading={busy}
          disabled={unticked || deleting}
          accessibilityHint={unticked ? t("auth.welcome.ageRequired") : undefined}
          onPress={submit}
        />
        {deleting ? (
          <Text
            style={styles.help}
            accessibilityLiveRegion="polite"
            aria-live="polite"
          >
            {t("auth.ageGate.deleting")}
          </Text>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: deleting }}
          {...stateAria({ disabled: deleting })}
          disabled={deleting}
          onPress={onSignOut}
          style={[styles.linkHit, deleting && styles.linkDisabled]}
        >
          <Text style={styles.link}>{t("app.signOut")}</Text>
        </Pressable>
        {onDeleteAccount ? (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ disabled: deleteOff }}
            {...stateAria({ disabled: deleteOff })}
            disabled={deleteOff}
            onPress={() => void deleteAccount()}
            style={[styles.linkHit, deleteOff && styles.linkDisabled]}
          >
            <Text style={styles.link}>{t("mobile.profile.deleteButton")}</Text>
          </Pressable>
        ) : null}
      </View>
    </ScrollView>
  );
}
